import React from "react";
import LegalPage from "./LegalPage";

export default function Privacy() {
  return (
    <LegalPage
      seoTitle="Privacy Policy"
      seoDescription="HelloSir.ai Privacy Policy — how we collect, process, store and protect your data, including GDPR rights, retention windows and third-party processors. A Cloud Lab Technologies (UK) product."
      seoKeywords={["hellosir.ai privacy policy", "AI chatbot GDPR", "AI calling agent privacy", "data protection SaaS", "Cloud Lab Technologies UK"]}
      path="/privacy"
      tagline="// privacy"
      heading="Privacy Policy"
    >
      <p>
        HelloSir.ai ("we", "us") is a SaaS product of{" "}
        <a href="https://www.cloudlabweb.com" target="_blank" rel="noopener noreferrer"><strong>Cloud Lab Technologies</strong></a>, a UK-registered software
        company. We take privacy seriously and only process the data we need to run the Service. This policy explains what data we collect, why we collect
        it, how long we keep it and the rights you have under the UK GDPR and EU GDPR.
      </p>

      <h2>1. Who is the data controller?</h2>
      <p>Cloud Lab Technologies (UK) is the data controller for personal data processed via HelloSir.ai. Contact: <a href="mailto:privacy@hellosir.ai">privacy@hellosir.ai</a>.</p>

      <h2>2. What we collect</h2>
      <ul>
        <li><strong>Account data</strong> — name, email, company, country, hashed password.</li>
        <li><strong>Billing data</strong> — invoices, top-up transactions, last 4 digits of card (held by our payment processor; we never see the full PAN).</li>
        <li><strong>Knowledge base content</strong> — pages, PDFs, documents and URLs that you upload or ask us to crawl to train your Agent.</li>
        <li><strong>Conversations</strong> — transcripts, voice recordings, IP, browser metadata and language locale of users who interact with your Agent.</li>
        <li><strong>Telemetry</strong> — basic, aggregated usage analytics to keep the Service healthy.</li>
      </ul>

      <h2>3. Why we process it (lawful basis)</h2>
      <ul>
        <li><strong>Contract</strong> — to provide the Service you signed up for.</li>
        <li><strong>Legitimate interest</strong> — fraud prevention, security, debugging and platform improvements.</li>
        <li><strong>Legal obligation</strong> — invoicing, tax, anti-money-laundering and law-enforcement requests.</li>
        <li><strong>Consent</strong> — where required (e.g. optional marketing emails).</li>
      </ul>

      <h2>4. AI processing & sub-processors</h2>
      <p>
        To generate replies, transcribe voice or synthesise speech, we send relevant portions of your content to upstream model providers under strict
        zero-retention agreements. We do not use your content to train any public foundation model. A current list of sub-processors is available on
        request.
      </p>

      <h2>5. International transfers</h2>
      <p>
        Data may be processed in the UK, EU and US under Standard Contractual Clauses and the UK International Data Transfer Addendum. Voice & chat traffic
        is encrypted in transit (TLS 1.2+) and at rest (AES-256).
      </p>

      <h2>6. Retention</h2>
      <ul>
        <li>Account data — retained while your account is active and for 12 months after closure for legal compliance.</li>
        <li>Conversation transcripts — 90 days by default; configurable down to 7 days or up to 2 years from your dashboard.</li>
        <li>Voice recordings — 30 days by default; configurable in settings.</li>
        <li>Billing records — 7 years (UK HMRC requirement).</li>
      </ul>

      <h2>7. Your rights</h2>
      <p>You have the right to access, rectify, erase, restrict or port your personal data, and to object to processing. Email <a href="mailto:privacy@hellosir.ai">privacy@hellosir.ai</a> with the subject "Data request". We respond within 30 days.</p>

      <h2>8. Cookies</h2>
      <p>
        We use strictly necessary cookies to keep you logged in and to remember consent preferences, plus optional analytics cookies (loaded only after consent).
        You can manage cookie preferences at any time from the footer banner.
      </p>

      <h2>9. Children</h2>
      <p>HelloSir is not intended for use by children under 16. We do not knowingly collect personal data from children.</p>

      <h2>10. Complaints</h2>
      <p>
        You can lodge a complaint with the UK Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>{" "}
        or your local EU supervisory authority.
      </p>
    </LegalPage>
  );
}
